import axios, { AxiosRequestHeaders } from "axios";

import store from "../redux/store"; // adjust to your actual path
import { logout } from "../redux/authSlice";

import io from "socket.io-client";
import { setExpiredModalOpen } from "../redux/features/expiredModalSlice";

const isProduction = true;
const isProductionSocket = true;

// Base URLs
export const baseURL = isProduction
  ? "https://lokalai-may25-production.vercel.app"
  : "http://localhost:5005";

export const baseSocketURL = isProductionSocket
  ? "https://socket-lokalai-production-may25.onrender.com/"
  : "http://localhost:6005/";

// export const baseSocketURL = "https://socket-lokalai-23jul.onrender.com/";

export const socketId = io(baseSocketURL, {
  transports: ["websocket"],
});

export const publicClient = axios.create({
  baseURL: `${baseURL}/api`,

  headers: {
    "Content-Type": "application/json",
  },
});

export const privateClient = axios.create({
  baseURL: `${baseURL}/api`,

  headers: {
    "Content-Type": "application/json",
  },
});

privateClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    if (!config.headers) {
      config.headers = {} as AxiosRequestHeaders;
    }
    (config.headers as AxiosRequestHeaders).Authorization = `Bearer ${token}`;
  }
  return config;
});

// Refresh logic

interface FailedRequest {
  resolve: (value?: string | null) => void;
  reject: (reason?: any) => void;
}
let isRefreshing = false;
let failedQueue: FailedRequest[] = [];

const processQueue = (error: any, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

privateClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // --- 1️⃣ Only act on 401 (unauthorized) responses ---
    const isUnauthorized = error.response?.status === 401;
    const message = error.response?.data?.message;

    // Only retry once, and only if token expired or invalid
    if (
      isUnauthorized &&
      !originalRequest._retry &&
      (message === "Token expired" || message === "Invalid token")
    ) {
      const refreshToken = localStorage.getItem("rftkn");

      // --- 2️⃣ No refresh token? -> Force logout immediately ---
      if (!refreshToken) {
        store.dispatch(logout());
        store.dispatch(setExpiredModalOpen(true));
        return Promise.reject(error);
      }

      // --- 3️⃣ Handle multiple requests waiting for refresh ---
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((newToken) => {
            // Re-attach token for the pending request
            originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
            return privateClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // --- 4️⃣ Ask backend for new access token ---
        const res = await publicClient.post("/v1/user/refresh-token", {
          refreshToken,
        });

        const { token: newToken, refreshToken: newRefresh } = res.data;
        if (!newToken) throw new Error("No new token returned");

        // --- 5️⃣ Save updated tokens ---
        localStorage.setItem("token", newToken);
        if (newRefresh) localStorage.setItem("rftkn", newRefresh);

        // --- 6️⃣ Update default headers ---
        privateClient.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${newToken}`;

        processQueue(null, newToken);

        originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
        return privateClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem("token");
        localStorage.removeItem("rftkn");
        store.dispatch(logout());
        store.dispatch(setExpiredModalOpen(true));
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// privateClient.interceptors.response.use(
//   (response) => response,
//   async (error) => {
//     const originalRequest = error.config;

//     if (error.response?.status === 401 && !originalRequest._retry) {
//       if (isRefreshing) {
//         return new Promise(function (resolve, reject) {
//           failedQueue.push({ resolve, reject });
//         })
//           .then((token) => {
//             originalRequest.headers["Authorization"] = "Bearer " + token;
//             return privateClient(originalRequest);
//           })
//           .catch((err) => Promise.reject(err));
//       }

//       originalRequest._retry = true;
//       isRefreshing = true;

//       try {
//         const refreshToken = localStorage.getItem("rftkn");
//         const res = await publicClient.post("/v1/user/refresh-token", {
//           refreshToken,
//         });

//         const newToken = res.data.token;
//         localStorage.setItem("token", newToken);

//         privateClient.defaults.headers.common["Authorization"] =
//           "Bearer " + newToken;

//         processQueue(null, newToken);

//         return privateClient(originalRequest);
//       } catch (err) {
//         processQueue(err, null);
//         store.dispatch(logout());
//         store.dispatch(setExpiredModalOpen(true));
//         return Promise.reject(err);
//       } finally {
//         isRefreshing = false;
//       }
//     }

//     return Promise.reject(error);
//   }
// );

// privateClient.interceptors.response.use(
//   (response) => response,
//   (error) => {
//     if (error.response && error.response.status === 401) {
//       console.log("🔴 AXIOS INTERCEPTOR TRIGGERED", error?.response?.status);

//       store.dispatch(logout());

//       store.dispatch(setExpiredModalOpen(true));
//       localStorage.removeItem("actkn");
//     }

//     return Promise.reject(error);
//   }
// );
